"use client";

interface FileViewerProps {
  url?: string | null;
  fileType?: string | null;
  error?: string;
}

function isTypeSupported(mimeType: string | undefined) {
  return [
    "url",
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/svg+xml", // not backend supported
    "text/plain",
    "text/csv",
    "application/json",
    "application/xml", // not backend supported
    "text/html",
    "video/mp4",
    "video/webm", // not backend supported
    "video/ogg", // not backend supported
    "audio/mpeg",
    "audio/ogg", // not backend supported
    "audio/wav",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
    "application/msword",
    "application/vnd.ms-powerpoint",
    "application/vnd.ms-excel",
  ].includes(mimeType || "");
}

function isVideoType(mimeType?: string) {
  return ["video/mp4", "video/webm", "video/ogg"].includes(mimeType || "");
}

function isAudioType(mimeType?: string) {
  return ["audio/mpeg", "audio/ogg", "audio/wav"].includes(mimeType || "");
}

function isOfficeType(mimeType?: string) {
  return [
    // Word
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",

    // Excel
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",

    // PowerPoint (legacy + modern)
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
  ].includes(mimeType || "");
}

function isImageType(mimeType?: string) {
  return [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
  ].includes(mimeType || "");
}

export function FileViewer({ url, fileType, error }: FileViewerProps) {
  if (error) {
    return <FileViewerError message={error} />;
  }

  if (fileType && !isTypeSupported(fileType)) {
    return (
      <FileViewerError
        message={`Unsupported file type${fileType ? ` (${fileType})` : ""}`}
      />
    );
  }

  if (!url) {
    return <FileViewerSkeleton />;
  }

  if (isVideoType(fileType)) {
    return (
      <video
        src={url}
        controls
        className="h-full w-full"
        title="Video Player"
      />
    );
  } else if (isAudioType(fileType)) {
    return <audio src={url} controls className="w-full" title="Audio Player" />;
  } else if (isOfficeType(fileType)) {
    // Use Google Docs Viewer for Office files
    const officeViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
      url
    )}&embedded=true`;
    return (
      <div className="relative h-[80vh] w-full overflow-hidden rounded-xl border border-theme bg-theme-0">
        <iframe
          src={officeViewerUrl}
          className="h-full w-full"
          title="Office Viewer"
        />
      </div>
    );
  } else if (isImageType(fileType)) {
    return (
      <div className="relative h-[80vh] w-full overflow-y-auto rounded-xl border bg-white">
        <img src={url} className="max-w-full h-auto" />
      </div>
    );
  } else {
    // Default to iframe for PDF, images, HTML, etc.
    // intentionally bg-white since plain HTML (i.e. course pages) will be rendered with black text and no background.
    return (
      <div className="relative h-[80vh] w-full overflow-hidden rounded-xl border bg-white">
        <iframe
          src={url}
          className="h-full w-full"
          loading="lazy"
          title="File Viewer"
        />
      </div>
    );
  }
}

function FileViewerError({ message }: { message: string }) {
  return (
    <div className="flex h-[40vh] w-full items-center justify-center rounded-xl border border-theme bg-theme-0">
      <div className="text-center">
        <div className="mb-2 text-sm font-medium text-theme-0">
          Unable to preview file
        </div>
        <div className="text-xs text-theme-1">{message}</div>
      </div>
    </div>
  );
}

function FileViewerSkeleton() {
  return (
    <div className="space-y-6">
      {/* Skeleton blocks */}
      <div className="space-y-4">
        <div className="h-4 w-3/4 rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-full rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-5/6 rounded bg-theme-1 animate-pulse" />
      </div>

      <div className="space-y-4">
        <div className="h-4 w-full rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-4/5 rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-3/4 rounded bg-theme-1 animate-pulse" />
      </div>

      <div className="space-y-4">
        <div className="h-4 w-5/6 rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-full rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-2/3 rounded bg-theme-1 animate-pulse" />
      </div>

      <div className="space-y-4">
        <div className="h-4 w-4/5 rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-full rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-3/4 rounded bg-theme-1 animate-pulse" />
      </div>

      <div className="space-y-4">
        <div className="h-4 w-full rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-5/6 rounded bg-theme-1 animate-pulse" />
        <div className="h-4 w-4/5 rounded bg-theme-1 animate-pulse" />
      </div>
    </div>
  );
}
